import type React from 'react';

import { FormField, FormHelp, Label, Select } from '@zillow/constellation-10';

import type { Section } from '../../..//models';
import { getOrdinal } from '../../../utils/humanReadable';
import { BASE_RENT, LATE_RENT } from '../../../constants';
import CurrencyInput from '../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

// Generate options from min to max days
const lateDaysOptions = Array.from({ length: LATE_RENT.BOUNDS.MAX_DAYS - LATE_RENT.BOUNDS.MIN_DAYS + 1 }, (_, i) => {
  const value = LATE_RENT.BOUNDS.MIN_DAYS + i;
  return { value: String(value), label: `${value} day${value === 1 ? '' : 's'}` };
});

export default function LateRent({ section, setSectionValues }: Props): JSX.Element {
  function onLateDaysChange(event: React.ChangeEvent<HTMLSelectElement>): void {
    setSectionValues({
      [LATE_RENT.FIELD_NAME.LATE_DAYS]: Number(event.target.value),
    });
  }

  function onLateFeeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LATE_RENT.FIELD_NAME.FEE_AMOUNT]: value,
    });
  }

  const lateDays = section.getFieldValue(LATE_RENT.FIELD_NAME.LATE_DAYS);
  const rentDueDateField = section.getField(BASE_RENT.FIELD_NAME.DUE_DATE);

  return (
    <>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>When will a late fee be charged for past due rent?</Label>}
        control={
          <Select value={lateDays != null ? String(lateDays) : ''} onChange={onLateDaysChange}>
            {lateDaysOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        }
        description={
          <>
            {rentDueDateField && rentDueDateField.isDefined() && (
              <FormHelp>
                Rent is due the
                {` ${getOrdinal(section.getFieldValue(BASE_RENT.FIELD_NAME.DUE_DATE))} `}
                of every month
              </FormHelp>
            )}
          </>
        }
      />
      <FormField
        required
        error={section.getField(LATE_RENT.FIELD_NAME.FEE_AMOUNT).hasError}
        label={<Label>What is the late fee?</Label>}
        control={
          <CurrencyInput
            value={section.getFieldValue(LATE_RENT.FIELD_NAME.FEE_AMOUNT)}
            onChange={onLateFeeChange}
            onBlur={onLateFeeChange}
            error={section.getField(LATE_RENT.FIELD_NAME.FEE_AMOUNT).hasError}
            endAdornment="flat fee"
          />
        }
        description={
          section.getField(LATE_RENT.FIELD_NAME.FEE_AMOUNT).hasError && (
            <FormHelp>{section.getField(LATE_RENT.FIELD_NAME.FEE_AMOUNT).getErrorMessage()}</FormHelp>
          )
        }
      />
    </>
  );
}
